import { ApiError, describeApiError } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { useEffect, useRef, type ReactNode } from 'react';
import type { ContestTransition } from '../../fantasy/contest';
import { Modal, btnGhost, btnPrimary } from '../Modal';

/**
 * §6's transitions, confirmed.
 *
 * <b>One dialog for all three, because the shape of the question is identical</b> — here is what
 * becomes true, here is what stops being possible, and none of it can be undone. What differs is
 * the list of claims, and those live in `CONTEST_TRANSITIONS` so that a claim and the button that
 * makes it true cannot part company.
 *
 * The refusal is rendered <b>inside the dialog</b> rather than behind it, which is the one
 * arrangement decision here. A publish refused for §6.1's already-started events is not a "that
 * didn't work" — it is the answer to the question the dialog asked, it names events, and it wants
 * reading. Closing the dialog to go and find it on the screen underneath is how somebody clicks
 * Publish three times.
 */
export function ContestTransitionDialog({
  contestName,
  transition,
  pending,
  error,
  extra,
  onClose,
  onConfirm,
}: {
  contestName: string;
  transition: ContestTransition;
  pending: boolean;
  /** The failed mutation, if the last attempt was refused. */
  error: unknown;
  /** Anything this particular transition has to say about *this* contest — what publish will pin. */
  extra?: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const refusal = refusalOf(error);

  // Both halves of the failure region as one string, so there is one thing to key the scroll on and
  // one place for the region to live. `describeApiError(null)` is null and `ErrorNotice` renders
  // nothing for null, so this is also exactly "was the last attempt refused".
  const notice = refusal ? refusal.message : describeApiError(error);
  const noticeRef = useRef<HTMLDivElement>(null);

  /**
   * A refusal that lands below the fold is a dead click.
   *
   * `Modal`'s body is the scrolling region (`flex-1 overflow-auto`) and the confirm button sits in
   * a footer *outside* it. So being refused appends the answer underneath four claims the operator
   * has just read — off screen — while the button they pressed stays exactly where it was, looking
   * exactly as it did. §6.1's refusal names up to ten events, which is the longest message here and
   * therefore the one most certain to be missed. Finding 5 of `13-fantasy-walk.md` is that shape:
   * the server refused correctly and nothing visibly happened.
   *
   * <b>Keyed on the message, not on the error.</b> TanStack hands back a fresh error object per
   * attempt, so keying on the object would yank the dialog out from under somebody re-reading the
   * same refusal every time they retried and got the same answer. A *different* refusal is a
   * different thing to read, and does deserve to move the eye.
   *
   * `block: 'nearest'` scrolls the least that will do, so a notice already in view stays put. The
   * reader who is not looking at the screen is served by `role="alert"`, which both branches carry;
   * this is only for the one who is.
   */
  useEffect(() => {
    if (notice === null) return;
    // The `behavior` option is not covered by the stylesheet's reduced-motion rules, so it is asked
    // here. A refused publish is not the moment to move somebody's viewport in a way they have
    // asked the machine not to.
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    noticeRef.current?.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
  }, [notice]);

  return (
    <Modal
      title={transition.title}
      subtitle={contestName}
      onClose={onClose}
      width="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            {/* Not "Cancel": the operator is choosing between now and later, not abandoning
                something. Matches the salary model's publish dialog. */}
            Not yet
          </button>
          <button type="button" onClick={onConfirm} disabled={pending} className={btnPrimary}>
            {pending ? 'Working…' : transition.confirm}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="space-y-2.5 text-sm leading-relaxed text-ink-800/80">
          {transition.claims.map((claim) => (
            <li key={claim}>{lead(claim)}</li>
          ))}
        </ul>

        {extra}

        {/* Rendered only when there is something to say: an always-present wrapper would take a
            `space-y-4` gap of its own and leave the dialog padded for a message it is not showing. */}
        {notice !== null ? (
          <div ref={noticeRef}>
            {refusal ? <Refusal refusal={refusal} /> : <ErrorNotice message={notice} />}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * A refusal worth reading rather than dismissing.
 *
 * `event_already_started` is the one this phase is asked to surface by name (§6.1), and the
 * server's message already carries the offending events — internal id and date, capped at ten with
 * a count. It is rendered <b>whole and verbatim</b>, the same rule the points breakdown's
 * explanation follows: the sentence was written where the facts are, and a client re-wording it
 * from a code would be a second implementation of §6.1 that nothing tests.
 *
 * The others are recognised only to say which section to go and fix, because "that request was
 * rejected" against a six-section form is a worse answer than the bug.
 */
const WHERE_TO_LOOK: Record<string, string> = {
  event_already_started:
    'Narrow the leaderboard’s scope in section 1 so it starts with an event that has not run, or point this contest at a series that is still to come.',
  locks_after_first_event: 'Section 5 — bring the lock time back to before the first event starts.',
  opens_after_lock: 'Section 5 — entry has to open before it closes.',
  pool_too_small: 'Section 4 — derive a larger pool, or lower the roster size in section 2.',
  scope_empty: 'Section 1 — check the leaderboard’s dates and filters.',
  invalid_pool: 'Section 4 — one or more prices were refused.',
  contest_not_open: 'This contest is not taking entries, so there is nothing to lock.',
  contest_not_locked: 'A contest closes from locked. Lock entries first.',
};

function Refusal({ refusal }: { refusal: { code: string; message: string } }) {
  const where = WHERE_TO_LOOK[refusal.code];

  return (
    <div role="alert" className="border border-line border-l-4 border-l-bad bg-surface p-3">
      <p className="text-[12.5px] leading-relaxed text-ink-900">{refusal.message}</p>
      {where ? <p className="mt-2 text-[11.5px] text-ink-800/60">{where}</p> : null}
    </div>
  );
}

/**
 * The server's own refusal, or null for anything else.
 *
 * Only 4xx with a code and a message: a 500 has nothing an operator can act on and a 403 is about
 * entitlement rather than about this contest, so both fall through to `describeApiError`, which
 * already tells those apart properly.
 */
function refusalOf(error: unknown): { code: string; message: string } | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status < 400 || error.status >= 500) return null;
  if (error.status === 401 || error.status === 403) return null;
  return error.code && error.serverMessage
    ? { code: error.code, message: error.serverMessage }
    : null;
}

/**
 * Bolds the opening sentence of a claim, so the list is scannable before it is read.
 *
 * Splitting on the first full stop rather than carrying markup in the strings keeps
 * `CONTEST_TRANSITIONS` plain text — which is what lets those sentences be read, checked and
 * changed without anybody editing JSX.
 */
function lead(claim: string): ReactNode {
  const end = claim.indexOf('. ');
  if (end === -1) return claim;
  return (
    <>
      <strong className="text-ink-950">{claim.slice(0, end + 1)}</strong>
      {claim.slice(end + 1)}
    </>
  );
}
